import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { ActionIcon, Alert, Button, Center, Checkbox, Group, Loader, Paper, Select, SimpleGrid, Stack, Table, Text, Title } from '@mantine/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { formatAmount, formatQuantity, todayIso } from '../format';
import { ScheduleSheet } from '../modules/scheduling/SchedulePrintPage';
import { PageHeader, Section } from '../ui/Section';
import type { Sheet } from './sheet';
import { type BuiltSheet, SHEET_KINDS, type SheetChoice, buildSheet } from './sheets';
import { downloadXlsx } from './xlsx';

const STORE = 'pst.mappe.choice';
function stored(): SheetChoice[] {
  try { return JSON.parse(window.localStorage.getItem(STORE) ?? '[]') as SheetChoice[]; } catch { return []; }
}
function store(list: SheetChoice[]): void {
  try { window.localStorage.setItem(STORE, JSON.stringify(list)); } catch { /* private window */ }
}

/**
 * The Mappe: several sheets from projects and overviews, in an order of one's
 * choosing, as one PDF (each sheet on its own page, through the browser's
 * print) or one Excel file (one worksheet each). The last composition is
 * kept, so the same Mappe is one click next time.
 */
export function MappePage(): JSX.Element {
  const queryClient = useQueryClient();
  const me = queryClient.getQueryData<{ permissions: string[] }>(['me']);
  const can = (p: string): boolean => me?.permissions.includes(p) ?? false;
  const projects = useQuery({ queryKey: ['projects'], queryFn: api.projects });
  const [choice, setChoice] = useState<SheetChoice[]>(stored);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [built, setBuilt] = useState<BuiltSheet[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => store(choice), [choice]);

  if (projects.isPending) return <Center h={200}><Loader /></Center>;
  const list = projects.data ?? [];
  const pid = projectId ?? list[0]?.id ?? null;
  const kinds = SHEET_KINDS.filter((k) => can(k.permission));
  const has = (c: SheetChoice): boolean => choice.some((x) => x.kind === c.kind && x.projectId === c.projectId);
  const toggle = (c: SheetChoice, on: boolean): void => { setBuilt(null); setChoice((l) => (on ? [...l, c] : l.filter((x) => !(x.kind === c.kind && x.projectId === c.projectId)))); };
  const move = (i: number, by: number): void => { setBuilt(null); setChoice((l) => { const n = [...l]; const [x] = n.splice(i, 1); n.splice(Math.max(0, Math.min(n.length, i + by)), 0, x!); return n; }); };
  const labelOf = (c: SheetChoice): string => {
    const p = list.find((x) => x.id === c.projectId);
    return `${p ? `${p.number} · ` : ''}${SHEET_KINDS.find((k) => k.kind === c.kind)?.label ?? c.kind}`;
  };
  const valid = choice.filter((c) => !c.projectId || list.some((p) => p.id === c.projectId));

  const build = async (): Promise<BuiltSheet[] | null> => {
    setBusy(true);
    setError(null);
    try {
      const sheets: BuiltSheet[] = [];
      for (const c of valid) sheets.push(await buildSheet(c, list));
      setBuilt(sheets);
      return sheets;
    } catch (e) {
      setError(`Ein Blatt ließ sich nicht laden: ${(e as Error).message}`);
      return null;
    } finally {
      setBusy(false);
    }
  };
  const pdf = async (): Promise<void> => {
    const sheets = built ?? (await build());
    if (!sheets) return;
    // Let the preview render before the print dialog takes its picture.
    window.setTimeout(() => window.print(), 300);
  };
  const excel = async (): Promise<void> => {
    const sheets = built ?? (await build());
    if (sheets) downloadXlsx(sheets, `PST Mappe ${todayIso()}.xlsx`);
  };

  return (
    <div style={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
      <Stack gap="md" pb="xl">
        <div className="pst-no-print">
          <PageHeader title="Mappe" description="Blätter aus Projekten und Übersichten zusammenstellen und als ein PDF oder eine Excel-Datei ausgeben. Die Zusammenstellung bleibt für das nächste Mal." />
        </div>
        {error && <Alert color="red" className="pst-no-print" withCloseButton onClose={() => setError(null)}>{error}</Alert>}
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md" className="pst-no-print">
          <Section title="Blätter wählen" description="Projekt wählen, dann die Blätter anhaken.">
            <Select label="Projekt" value={pid} onChange={setProjectId} allowDeselect={false} searchable data={list.map((p) => ({ value: p.id, label: `${p.number} – ${p.name}` }))} />
            <Stack gap={4}>
              {pid && kinds.filter((k) => k.perProject).map((k) => (
                <Checkbox key={k.kind} label={k.label} checked={has({ kind: k.kind, projectId: pid })} onChange={(e) => toggle({ kind: k.kind, projectId: pid }, e.currentTarget.checked)} />
              ))}
            </Stack>
            <Text size="sm" fw={600} mt="xs">Über alle Projekte</Text>
            <Stack gap={4}>
              {kinds.filter((k) => !k.perProject).map((k) => (
                <Checkbox key={k.kind} label={k.label} checked={has({ kind: k.kind })} onChange={(e) => toggle({ kind: k.kind }, e.currentTarget.checked)} />
              ))}
            </Stack>
          </Section>
          <Section
            title={`Mappe (${valid.length} ${valid.length === 1 ? 'Blatt' : 'Blätter'})`}
            description="In dieser Reihenfolge; im PDF beginnt jedes Blatt auf einer neuen Seite."
            actions={
              <>
                <Button size="xs" variant="default" disabled={!valid.length} loading={busy} onClick={() => void build()}>Vorschau</Button>
                <Button size="xs" disabled={!valid.length} loading={busy} onClick={() => void pdf()}>PDF</Button>
                <Button size="xs" disabled={!valid.length} loading={busy} onClick={() => void excel()}>Excel</Button>
              </>
            }
          >
            {valid.length === 0 && <Text size="sm" c="dimmed">Noch keine Blätter gewählt.</Text>}
            {valid.map((c, i) => (
              <Group key={`${c.kind}-${c.projectId ?? ''}`} justify="space-between" wrap="nowrap" gap="xs">
                <Text size="sm" style={{ minWidth: 0 }} lineClamp={1}>{i + 1}. {labelOf(c)}</Text>
                <Group gap={2} wrap="nowrap">
                  <ActionIcon size="sm" variant="subtle" color="gray" aria-label="nach oben" disabled={i === 0} onClick={() => move(i, -1)}>↑</ActionIcon>
                  <ActionIcon size="sm" variant="subtle" color="gray" aria-label="nach unten" disabled={i === valid.length - 1} onClick={() => move(i, 1)}>↓</ActionIcon>
                  <ActionIcon size="sm" variant="subtle" color="gray" aria-label="entfernen" onClick={() => toggle(c, false)}>✕</ActionIcon>
                </Group>
              </Group>
            ))}
            {valid.length > 0 && <Button size="compact-xs" variant="subtle" color="gray" onClick={() => { setChoice([]); setBuilt(null); }} style={{ alignSelf: 'flex-start' }}>alle entfernen</Button>}
          </Section>
        </SimpleGrid>

        {built && (
          <Stack gap="lg" className="pst-mappe">
            {built.map((s, i) => (
              <Paper key={i} withBorder p="md" className="pst-sheet">
                {s.schedule ? <ScheduleSheet title={s.title} plan={s.schedule.plan} schedule={s.schedule.schedule} /> : <SheetView sheet={s} />}
              </Paper>
            ))}
          </Stack>
        )}
      </Stack>
    </div>
  );
}

/** One sheet as a plain table, for screen and paper alike. */
export function SheetView({ sheet }: { sheet: Sheet }): JSX.Element {
  const bold = new Set(sheet.boldRows ?? []);
  const show = (v: string | number | null, format?: string): string =>
    v === null ? '' : typeof v === 'number' ? (format === 'amount' ? formatAmount(v) : format === 'int' ? String(Math.round(v)) : formatQuantity(v)) : v;
  return (
    <Stack gap={4}>
      <Title order={4}>{sheet.title}</Title>
      {sheet.subtitle && <Text size="xs" c="dimmed">{sheet.subtitle}</Text>}
      <Table verticalSpacing={2} className="pst-tabular pst-sheet-table" withTableBorder={false}>
        <Table.Thead>
          <Table.Tr>{sheet.columns.map((c, i) => <Table.Th key={i} ta={c.format ? 'right' : undefined}>{c.label}</Table.Th>)}</Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {sheet.rows.map((r, i) => (
            <Table.Tr key={i} style={bold.has(i) ? { fontWeight: 700, background: 'var(--mantine-color-default-hover)' } : undefined}>
              {r.map((v, j) => (
                <Table.Td key={j} ta={typeof v === 'number' ? 'right' : undefined} style={{ whiteSpace: typeof v === 'number' ? 'nowrap' : 'pre-line' }}>
                  <Text size="xs" fw={bold.has(i) ? 700 : undefined}>{show(v, sheet.columns[j]?.format)}</Text>
                </Table.Td>
              ))}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Stack>
  );
}
